"use client";

import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { Button } from '@/src/components/ui/button'
import { listEnvironmentSettings, upsertEnvironmentSetting } from '@/src/api/environmentSetting'
import { toast } from 'sonner'
import { BannerEditor } from '@/src/components/manage/BannerEditor'
import { FloppyDiskIcon } from '@phosphor-icons/react'

export const Route = createFileRoute('/_manage/manage/admin/dashboard/announcements')({
  component: AnnouncementsPage,
})

function AnnouncementsPage() {
  const queryClient = useQueryClient()
  const [bannerValue, setBannerValue] = useState<{ html: string; color: string; markdown: string }>({
    html: '',
    color: '#fef3c7',
    markdown: '',
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [originalValue, setOriginalValue] = useState<{ html: string; color: string; markdown: string } | null>(null)

  const { data: settings, isLoading } = useQuery({
    queryKey: ['environmentSettings'],
    queryFn: () => listEnvironmentSettings(),
  })

  const upsertMutation = useMutation({
    mutationFn: (data: { key: string; value: any; description?: string }) =>
      upsertEnvironmentSetting({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environmentSettings'] })
      toast.success('Banner saved successfully')
      setHasChanges(false)
      setOriginalValue(bannerValue)
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save banner')
    },
  })

  // Load existing settings
  useEffect(() => {
    if (settings) {
      const banner = settings.find((s: any) => s.key === 'global_banner')
      if (banner?.value) {
        const value = banner.value as { html?: string; color?: string; markdown?: string }
        const loadedValue = {
          html: value.html || '',
          color: value.color || '#fef3c7',
          markdown: value.markdown || value.html || '',
        }
        setBannerValue(loadedValue)
        setOriginalValue(loadedValue)
      } else {
        // No existing banner - set original to empty state
        const emptyValue = {
          html: '',
          color: '#fef3c7',
          markdown: '',
        }
        setOriginalValue(emptyValue)
      }
    }
  }, [settings])

  // Track changes
  const handleChange = (newValue: { html: string; color: string; markdown: string }) => {
    setBannerValue(newValue)
    if (originalValue) {
      const changed = newValue.markdown !== originalValue.markdown || 
                     newValue.color !== originalValue.color
      setHasChanges(changed)
    } else {
      // No original value yet, check if there's any content
      setHasChanges(newValue.markdown.trim() !== '' || newValue.color !== '#fef3c7')
    }
  }

  const handleSave = () => {
    upsertMutation.mutate({
      key: 'global_banner',
      value: bannerValue,
      description: 'Global banner displayed on homepage',
    })
  }

  const handleReset = () => {
    if (originalValue) {
      setBannerValue(originalValue)
      setHasChanges(false)
    }
  }

  return (
    <div className="space-y-6">
      <BannerEditor
        value={bannerValue}
        onChange={handleChange}
        disabled={isLoading}
      />
      <div className="flex justify-end gap-2">
        {hasChanges && (
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={upsertMutation.isPending || isLoading}
          >
            Discard Changes
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={upsertMutation.isPending || isLoading || !hasChanges}
        >
          <FloppyDiskIcon className="h-4 w-4 mr-2" />
          {upsertMutation.isPending ? 'Saving...' : 'Save Banner'}
        </Button>
      </div>
    </div>
  )
}
